// use-hyperliquid-global — WebSocket-Hook für Hyperliquid Live-Daten + Whale-Detection.
// V1-Pendant: src/hooks/useHyperliquidGlobal.js.
//
// Verantwortungen:
// 1. WS-Connect zu `wss://api.hyperliquid.xyz/ws` mit Reconnect (3s Delay).
// 2. Subscribe auf `allMids` + 5 Coins (BTC/ETH/SOL/xyz:GOLD/xyz:SILVER).
// 3. Parse incoming Messages → Update Store (1s throttle für allMids).
// 4. Whale-Detection ≥ $1M → onWhaleTrade-Callback feuern.
//
// V3-Refactor: WebSocket-Factory injectable für Tests.

import { useEffect, useRef } from 'react';
import {
  HL_WS_URL,
  RECONNECT_DELAY_MS,
  STATE_UPDATE_THROTTLE_MS,
  buildSubscribeMessages,
  parseAllMids,
  parseAssetCtx,
  parseTradesForWhales,
  safeParseMessage,
} from '@/lib/hyperliquid-ws';
import {
  selectIsConnected,
  selectLiveMetrics,
  selectLivePrices,
  useHyperliquidStore,
} from '@/stores/hyperliquid-store';
import type { HLLiveMetrics, HLLivePrices, WhaleAlert } from '@/types/hyperliquid';

export interface UseHyperliquidGlobalOptions {
  /** Wird bei jedem Trade ≥ $1M aufgerufen. */
  onWhaleTrade?: (alert: WhaleAlert) => void;
  /** Custom WebSocket factory (Test-Hilfe — Default: native WebSocket). */
  websocketFactory?: (url: string) => WebSocket;
  /** Wenn `false`, wird kein WS aufgebaut (Test-Hilfe). */
  enabled?: boolean;
}

export interface UseHyperliquidGlobalReturn {
  livePrices: HLLivePrices;
  liveMetrics: HLLiveMetrics;
  isConnected: boolean;
}

export function useHyperliquidGlobal(
  options: UseHyperliquidGlobalOptions = {},
): UseHyperliquidGlobalReturn {
  const { onWhaleTrade, websocketFactory, enabled = true } = options;
  const livePrices = useHyperliquidStore(selectLivePrices);
  const liveMetrics = useHyperliquidStore(selectLiveMetrics);
  const isConnected = useHyperliquidStore(selectIsConnected);

  const onWhaleTradeRef = useRef(onWhaleTrade);
  useEffect(() => {
    onWhaleTradeRef.current = onWhaleTrade;
  }, [onWhaleTrade]);

  useEffect(() => {
    if (!enabled) return undefined;

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    const pricesBuffer: HLLivePrices = {};
    let lastUpdate = 0;

    const flushPrices = (): void => {
      const now = Date.now();
      if (now - lastUpdate <= STATE_UPDATE_THROTTLE_MS) return;
      useHyperliquidStore.getState().setPrices({ ...pricesBuffer });
      lastUpdate = now;
    };

    const connect = (): void => {
      if (cancelled) return;
      const factory = websocketFactory ?? ((u: string): WebSocket => new WebSocket(u));
      ws = factory(HL_WS_URL);

      ws.onopen = (): void => {
        useHyperliquidStore.getState().setConnected(true);
        for (const sub of buildSubscribeMessages()) {
          ws?.send(sub);
        }
      };

      ws.onmessage = (e: MessageEvent): void => {
        const raw = typeof e.data === 'string' ? e.data : '';
        if (!raw) return;
        const msg = safeParseMessage(raw);
        if (!msg) return;

        useHyperliquidStore.getState().setLastMessageAt(Date.now());

        const mids = parseAllMids(msg);
        if (mids) {
          Object.assign(pricesBuffer, mids);
          flushPrices();
          return;
        }

        const ctx = parseAssetCtx(msg);
        if (ctx) {
          useHyperliquidStore.getState().setMetric(ctx.coin, ctx);
          return;
        }

        if (msg.channel === 'trades') {
          const whales = parseTradesForWhales(msg);
          const handler = onWhaleTradeRef.current;
          if (handler) {
            for (const w of whales) handler(w);
          }
        }
      };

      ws.onclose = (): void => {
        useHyperliquidStore.getState().setConnected(false);
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      };

      ws.onerror = (): void => {
        // V1: silent — onclose triggert Reconnect
      };
    };

    connect();

    return (): void => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        // Reconnect-Loop verhindern beim Cleanup
        ws.onclose = null;
        if (ws.readyState !== WebSocket.CLOSED) {
          ws.close();
        }
      }
      useHyperliquidStore.getState().setConnected(false);
    };
  }, [enabled, websocketFactory]);

  return { livePrices, liveMetrics, isConnected };
}
